import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import { Match2RewardsPanel } from './Match2RewardsPanel';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { PetEntity } from 'src/game/logic/EntityManager';
import StateObserver, { now, user } from 'src/StateObserver';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import sounds from 'src/lib/sounds';
import { GCInstant } from 'src/lib/gcinstant';
import { openNativeAppStorePage } from 'src/game/native/native.util';
import { saveMatch2GameplayState } from '../savestate/match2-savestate.utils';
import { getMatch2PredefinedReward } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { InstallNativeSequenceArgs } from 'src/game/native/native.sequence';
import { trackSessionStateSave } from 'src/game/crossplay/crossplay.tracking';
import { getRewardsId } from 'src/replicant/native/native.getters';
import { track } from 'src/lib/analytics/utils';

export default class PopupMatch2PlayOnApp extends PopupMatch2Base {
  private pet: PetEntity;
  private species: PetSpeciesID;
  private rewards: Match2RewardsPanel;
  private args!: InstallNativeSequenceArgs;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close(),
      title: {
        type: 'banner',
        localeText: () => i18n('match2.native.title'),
        textSize: 40,
        textOffsetY: 26,
      },
      cta: {
        localeText: () => i18n('match2.native.install').toLocaleUpperCase(),
        width: 300,
        positioning: 'edge',
        onClick: async () => {
          await this.openStore();
          return opts.close();
        },
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PLAY_ON_APP_POPUP,
      },
    });

    const bubble = new ImageScaleView({
      superview: this.panel,
      image: 'assets/ui/match2/native/control/bubble_native.png',
      width: 522,
      height: 157,
      centerAnchor: true,
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5 - 5,
      y: 135,
    });

    new LangBitmapFontTextView({
      superview: bubble,
      font: 'Body',
      size: 27,
      color: '#000000',
      verticalAlign: 'center',
      centerAnchor: true,
      centerOnOrigin: true,
      width: bubble.style.width - 120,
      height: bubble.style.height - 60,
      x: bubble.style.width * 0.5 + 40,
      y: bubble.style.height * 0.5 - 7,
      localeText: () => i18n('match2.native.message'),
      wordWrap: true,
    });

    this.rewards = new Match2RewardsPanel({
      superview: this.panel,
      width: 312,
      height: 171,
      x: this.panel.style.width * 0.5 + 100,
      y: this.panel.style.height * 0.5 + 70,
    });

    this.species =
      Pet.getters.getCurrentEvolution(StateObserver.getState().user)?.species ??
      'dog'; // Falls back to dog if no pet is found - in the case of popup showing up prior to the box opening.
    this.pet = new PetEntity('match2NativePopup', this.species);
    this.pet.updateOpts({
      superview: this.panel,
      x: 145,
      y: 390,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });
  }

  public override init(
    opts: PopupMatch2BaseInitOptions & InstallNativeSequenceArgs,
  ) {
    super.init(opts);
    const petAnimations = petAnimationNames[this.species];
    this.pet.setAnimation(petAnimations.waive);
    const rewards = getMatch2PredefinedReward(getRewardsId(user()));
    this.rewards.setRewards(rewards);
    this.args = opts;
  }

  /**
   * Open native app store
   */
  private async openStore() {
    sounds.sfx.play('ok');

    // Save current gameplay state, in case it happens during gameplay.
    await saveMatch2GameplayState('playOnApp');

    const timestamp = now();

    trackSessionStateSave({
      origin: this.args.originFeature,
      feature: 'match2_play_on_app_popup',
      subFeature: 'match2_play_on_app_popup_cta',
      timestamp,
    });

    track('GoToStore_Button_click', {
      deviceType: this.getPlatformForAnalytics(),
    });

    openNativeAppStorePage({
      payload:
        this.args.originFeature === 'low_gem_balance'
          ? {
              $deepLink: [
                ...(this.args.additionalContext ?? []),
                {
                  type: 'startGemsPinchSequence',
                  feature: this.args.feature,
                  pinch: this.args.pinch,
                  gemsNeeded: this.args.gemsNeeded,
                },
              ],
            }
          : undefined,
      tracking: {
        origin: this.args.originFeature,
        feature: 'match2_play_on_app_popup',
        subFeature: 'match2_play_on_app_popup_cta',
        timestamp,
      },
    });
  }

  /**
   * Analytics helper
   */
  private getPlatformForAnalytics(): 'ios' | 'android' | 'unknown' {
    switch (GCInstant.osType) {
      case 'IOS':
      case 'WEB_IOS':
        return 'ios';
      case 'ANDROID':
      case 'WEB_ANDROID':
        return 'android';
      default:
        return 'unknown';
    }
  }
}
